import { useEffect, useRef, useState } from 'react';
import beTwMerge from '@/utils/beTwMerge';
import { format } from 'date-fns';
import { getFormattedDate, getOriginalDate } from '@/utils/utils';
import { dataVirtual } from '@/utils/constants';
import Select from '@/components/Select/Select';
import useStore from '@/store/useStore';
import useMediaQuery from '@/hooks/useMediaQuery';
import { DAYS_OF_DATE_NIGHT } from '@/consts/appointment';
import Button from '@/components/homepage/Button';
import { Calendar } from '@/components/AppointmentBookingModal/Calendar';
import { LoadingSpinner } from '@/components/Loading';

// This component is responsible for rendering the third page of the appointment booking modal.
// It includes the selection of a date, time, and timezone.
export default function PageThree({
  info,
  setInfo,
  setConfirmDateError,
}: {
  info: any;
  setInfo: (key: string, value: any) => void;
  setConfirmDateError: (value: boolean) => void;
}) {
  const virtualAppointmentID = dataVirtual.id;
  const timeContainerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const getSetsterAvailabilityDateTimes = useStore(state => state.getSetsterAvailabilityDateTimes);
  const getSetsterAvailabilityDateNightTimes = useStore(state => state.getSetsterAvailabilityDateTimes);
  const getSetsterProviderId = useStore(state => state.getSetsterProviderId);
  const isCity = info.selectedShowroomStore.modalName !== 'Virtual Appointment';
  const [isLoading, setIsLoading] = useState(true);
  const [areTimesLoading, setAreTimesLoading] = useState(false);
  const times = useStore(state => state.appointmentBookingInformation.setsterAvailabilityDateTimes);
  const dateNightTimes = useStore(state => state.appointmentBookingInformation.setsterAvailabilityDateNightTimes);
  const isVirtual = !isCity || info.selectedCity === 'Virtual Appointment';

  const parentRef = useRef<HTMLDivElement>(null);

  // Timezone abbreviations and their corresponding names and IDs
  const timezones = {
    PT: '553',
    ET: '548',
    CT: '546',
    MT: '552',
    AKT: '543',
    HT: '549',
  };
  const timezoneNames = {
    PT: 'America/Los_Angeles',
    ET: 'US/Eastern',
    CT: 'US/Central',
    MT: 'US/Mountain',
    AKT: 'US/Alaska',
    HT: 'US/Hawaii',
  };
  const tzNames = {
    PT: 'Pacific Time',
    ET: 'Eastern Time',
    CT: 'Central Time',
    MT: 'Mountain Time',
    AKT: 'Alaska Time',
    HT: 'Hawaii Time',
  };

  // Default selected timezone is Pacific Time (PT)
  const [selectedOption, setSelectedOption] = useState(
    info.selectedTimezone
      ? {
          label: tzNames[info.selectedTimezone],
          value: info.selectedTimezone,
        }
      : { label: 'Pacific Time (PT)', value: 'PT' }
  );

  // Function to scroll to the time container
  function scrollToTime() {
    if (timeContainerRef.current && !isDesktop) {
      setTimeout(() => {
        timeContainerRef?.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }

  const findNextAvailableDay = (
    availabilityTimes: Record<string, { id: string; start: string; end: string }[]>,
    selectedDay: string
  ): Date => {
    const parameter =
      Object.keys(availabilityTimes).find(appointmentDate => appointmentDate >= selectedDay) || selectedDay;

    return getOriginalDate(parameter);
  };

  // Effect to set the first available date
  useEffect(() => {
    if (info.isSetsterError) {
      setIsLoading(false);
      return;
    }
    if (Object.keys(times).length) {
      setIsLoading(false);
      setAreTimesLoading(false);
      const today = getFormattedDate(new Date());
      let firstAvailableDate = findNextAvailableDay(times, today);
      if (Object.keys(dateNightTimes).length) {
        const dateNightFirstAvailable = findNextAvailableDay(dateNightTimes, today);
        firstAvailableDate =
          firstAvailableDate > dateNightFirstAvailable ? dateNightFirstAvailable : firstAvailableDate;
      }
      setInfo('firstAvailableDate', firstAvailableDate);
    }
  }, [times, dateNightTimes, info.isSetsterrror]);

  // Format the selected date
  const formattedDate = getFormattedDate(info.selectedDate);
  const timesToIterate = times[formattedDate];
  const dateNightTimesToIterate = dateNightTimes[formattedDate];

  // Function to format the time
  const formatTime = (time: string, isShowAMPM: boolean = true) => {
    const displayTime = `${+time.slice(0, 2) > 12 ? `${+time.slice(0, 2) - 12}:${time.slice(3, 5)}` : time}`;
    return isShowAMPM ? `${displayTime} ${+time.slice(0, 2) >= 12 ? 'PM' : 'AM'}` : displayTime;
  };

  // Function to format the selected time
  const formatSelectedTime = (time: { id: string; start: string; end: string }) => {
    const result = `${formatTime(time.start, false)} - ${formatTime(time.end)}`;

    return result;
  };

  const renderNextAvailable = (isDateNight: boolean) => (
    <Button
      className="tw-text-sm tw-text-be-dark-gray tw-font-avenirmedium tw-col-span-full tw-text-left"
      label="View Next Available"
      onClick={() => {
        const nextAvailableDate = findNextAvailableDay(
          isDateNight ? dateNightTimes : times,
          getFormattedDate(info.selectedDate)
        );
        (window as any).dataLayer.push({
          event: 'appointment_form',
          showroom_page_section: 'select date and time',
          showroom_action: 'view next available',
          showroom_location: info.selectedShowroomStore.modalName,
          general_event_details: isDateNight ? 'Date Night Experience' : 'Standard Appointment',
        });

        setInfo('selectedDate', nextAvailableDate);
        setInfo('selectedTime', '');
      }}
      type="link"
    />
  );

  const renderSelectTimeButton = (isDateNight: boolean) => {
    const availableTimes = isDateNight ? dateNightTimesToIterate : timesToIterate;
    const showroomAction = isDateNight ? 'time selection|Date Night Experience' : 'time selection|Standard Appointment';
    return availableTimes.map(time => (
      <Button
        className={beTwMerge(
          'tw-py-[12px] tw-px-2 tw-cursor-pointer tw-max-h-[40px] tw-tracking-normal tw-leading-normal tw-text-be-dark-gray tw-text-xs hover:tw-bg-white hover:tw-text-be-dark-gray hover:tw-border-color-light-green-cta tw-border-[#D9D9D9] active:tw-bg-white focus:tw-bg-white active:tw-text-be-dark-grey focus:tw-text-be-dark-grey',
          time.id === info.selectedTimeId && 'tw-bg-white tw-text-be-dark-gray tw-border-color-light-green-cta'
        )}
        key={time.id}
        label={formatSelectedTime(time)}
        onClick={() => {
          setInfo('selectedTime', formatSelectedTime(time));
          setInfo('selectedTimeId', time.id);
          setInfo('isSelectedDateNightTime', isDateNight);

          setConfirmDateError(false);
          getSetsterProviderId(isDateNight);

          (window as any).dataLayer.push({
            event: 'appointment_form',
            showroom_page_section: 'select date and time',
            showroom_action: showroomAction,
            showroom_location: info.selectedShowroomStore.modalName,
            general_event_details: formatTime(time.start),
          });

          setInfo('activePage', info.activePage + 1);
        }}
        type="secondary"
      />
    ));
  };

  const renderStandardAppointmentHeader = () => {
    let Header = 'Standard Appointment: 50 Minutes';

    if (info.selectedServiceName === 'Pick Up') {
      Header = 'Pick Up (15 Minutes)';
    }
    if (info.selectedServiceName === 'Drop Off') {
      Header = 'Drop Off (15 Minutes)';
    }
    return (
      <div className="tw-text-sm tw-text-be-dark-gray tw-font-avenirmedium tw-col-span-full tw-mt-4 lg:tw-mt-0">
        {Header}
      </div>
    );
  };

  const renderStandardAppointmentTimes = () => {
    if (!timesToIterate?.length)
      return (
        <>
          <div className="tw-col-span-full tw-text-sm tw-font-book tw-text-color-grey-700 tw-mb-2">
            We do not have any standard appointments available on your selected date.
          </div>
          {renderNextAvailable(false)}
        </>
      );
    return renderSelectTimeButton(false);
  };

  const renderDateNightHeader = () => {
    const isDateNight = DAYS_OF_DATE_NIGHT.includes(
      info.selectedDate?.toLocaleString('en-US', { weekday: 'short' }) || ''
    );

    let text = '';
    if (!dateNightTimesToIterate?.length) {
      text = 'Unfortunately, your selected date is fully booked.';
    }
    if (!isDateNight) {
      text = 'Date Nights are only offered Thursday - Saturday.';
    }
    const isAvailable = dateNightTimesToIterate?.length && isDateNight;

    return (
      <div className={beTwMerge('tw-mt-6 tw-col-span-full tw-mb-2 lg:tw-mb-0', isAvailable && 'lg:tw-mb-2')}>
        <div className="tw-text-sm tw-text-be-dark-gray tw-font-avenirmedium tw-mb-2">
          Date Night Experience: 75 Minutes
        </div>
        <div className="tw-text-sm tw-font-book tw-text-color-grey-700">
          <div className={beTwMerge('', text && 'tw-mb-7 lg:tw-mb-4')}>
            Every eternity begins with a date. During our exclusive experience for two, you&apos;ll design your dream
            ring with expert guidance, bites, cocktails, photos, and more.
          </div>
          {text}
        </div>
      </div>
    );
  };

  const renderDateNight = () => {
    if (
      isVirtual ||
      info.selectedServiceName === 'Drop Off' ||
      info.selectedServiceName === 'Pick Up' ||
      !Object.keys(dateNightTimes).length
    )
      return null;

    return (
      <>
        {renderDateNightHeader()}
        {!dateNightTimesToIterate?.length ? renderNextAvailable(true) : renderSelectTimeButton(true)}
      </>
    );
  };

  // Check if there are no appointment times
  const hasAppointmentTimes = !!timesToIterate?.length || !!dateNightTimesToIterate?.length;

  const renderDateAndTime = () => {
    if (info.isSetsterError)
      return (
        <div className="tw-flex tw-pt-4 tw-w-full tw-justify-center tw-col-span-2">
          <div className="tw-max-w-[670px] tw-text-sm tw-text-center lg:tw-text-lg tw-text-[#C21419]">
            We&#39;re having trouble loading available times right now. You can try again in a moment, or&nbsp;
            <a className="tw-underline !tw-text-[#C21419]" href="/contact">
              contact us
            </a>
            &nbsp;and we&#39;ll be happy to help you schedule.
          </div>
        </div>
      );

    return (
      <>
        <Calendar info={info} setInfo={setInfo} scrollToTime={scrollToTime} />

        <div className="tw-h-full tw-flex tw-flex-col tw-gap-y-4 tw-justify-between lg:tw-relative">
          <h3 className="tw-uppercase tw-pt-6 tw-px-4 tw-font-[500] tw-text-sm tw-text-center tw-font-[Avenir] tw-m-0 lg:tw-hidden tw-text-be-dark-gray">
            Select a time
          </h3>
          <div className="tw-flex tw-flex-col tw-justify-between lg:tw-overflow-y-auto tw-scrollbar-none ">
            {!info.setsterAvailabilityDateTimesLoading && (
              <div className="lg:tw-h-[416px] tw-scroll-m-[203px]" ref={timeContainerRef}>
                <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 tw-px-4 tw-gap-2 tw-scrollbar-none">
                  {renderStandardAppointmentHeader()}
                  {!hasAppointmentTimes ? (
                    <span className="tw-w-full tw-text-center tw-col-span-2 tw-font-[350] tw-font-book tw-text-sm tw-text-be-dark-gray">
                      We&apos;re sorry, there are no available appointment times on the date selected. Please select a
                      different date.{' '}
                    </span>
                  ) : (
                    <>
                      {isVirtual && (
                        <div className="tw-col-span-full tw-relative">
                          <Select
                            parentRef={parentRef}
                            className="[&>button>div]:tw-h-[40px] lg:tw-static lg:[&>div]:tw-top-[39px] [&>button>div]:tw-flex [&>button>div]:tw-items-center tw-text-be-dark-gray hover:[&>div>div]:!tw-bg-[#E7EFEF] [&>div>div[data-selected='true']]:!tw-text-[#0D6363] [&>div>div[data-selected='true']]:!tw-bg-transparent"
                            selectedOption={selectedOption}
                            onChange={({ label, value }) => {
                              setAreTimesLoading(true);
                              setSelectedOption({ label, value: value as string });
                              setInfo('selectedTimezone', value as string);
                              setInfo('selectedTimezoneName', timezoneNames[value as string]);
                              setInfo('selectedTimezoneId', timezones[value as string]);
                              setInfo('selectedShowroomStore', {
                                ...info.selectedShowroomStore,
                                timezoneAbreviation: value,
                              });
                              getSetsterAvailabilityDateTimes(
                                info.selectedServiceId,
                                info.selectedShowroomStore.id,
                                format(new Date(), 'yyyy-MM-dd'),
                                timezones[value as string]
                              );
                            }}
                            showDefaultValue={false}
                            items={[
                              {
                                label: 'Eastern Time (ET)',
                                value: 'ET',
                              },
                              {
                                label: 'Central Time (CT)',
                                value: 'CT',
                              },
                              {
                                label: 'Mountain Time (MT)',
                                value: 'MT',
                              },
                              {
                                label: 'Pacific Time (PT)',
                                value: 'PT',
                              },
                              {
                                label: 'Alaska Time (AKT)',
                                value: 'AKT',
                              },
                              {
                                label: 'Hawaii Time (HT)',
                                value: 'HT',
                              },
                            ]}
                            placeholder="Select Timezone"
                          />
                        </div>
                      )}
                      {!areTimesLoading ? (
                        <>
                          {renderStandardAppointmentTimes()}
                          {renderDateNight()}
                        </>
                      ) : (
                        <div className="tw-flex tw-justify-center tw-w-full tw-col-span-2 md:tw-col-span-3 tw-pt-4 tw-h-[344px] lg:tw-h-full">
                          <LoadingSpinner />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // Render the component
  return (
    <div
      ref={parentRef}
      className="tw-pt-4 tw-flex tw-flex-col tw-gap-y-4 lg:tw-h-[500px] tw-overflow-hidden tw-scrollbar-none lg:tw-gap-x-0 lg:tw-max-w-[902px] lg:tw-mx-auto lg:tw-min-h-[554px] lg:tw-max-h-[554px]"
    >
      <h3 className="tw-uppercase tw-px-4 tw-font-[500] tw-text-sm tw-text-center tw-m-0 lg:tw-hidden tw-text-be-dark-gray tw-font-[Avenir] !tw-tracking-[0.7px] ">
        Select a date
      </h3>
      <h3 className="tw-hidden tw-uppercase tw-px-4 tw-font-[500] tw-text-lg tw-text-center tw-m-0 lg:tw-block tw-text-be-dark-gray tw-font-[Avenir] !tw-tracking-[0.9px]">
        Select a date & time
      </h3>

      <div className="lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-x-6 tw-h-full lg:tw-h-auto">
        {isLoading ? (
          <div className="tw-flex tw-mt-12 tw-w-full tw-justify-center tw-col-span-2">
            <LoadingSpinner />
          </div>
        ) : (
          renderDateAndTime()
        )}
      </div>
      <div className="tw-px-4 tw-pt-4 lg:tw-col-start-2 lg:tw-max-h-[42px]">
        <p className="tw-m-0 tw-text-sm tw-text-color-grey-500 tw-text-center tw-font-[400]">
          Looking for another date & time?
        </p>

        <div className="tw-mx-auto tw-w-fit tw-divide-x tw-mt-0 tw-p-0 [&_button]:tw-border-l-[#D9D9D9] [&_a]:tw-border-l-[#D9D9D9] [&_button]:tw-h-[17px] [&_a]:tw-h-[17px]">
          {info.selectedShowroomStore.modalName === 'Virtual Appointment' &&
          info.prevSelectedShowroomStore.modalName === 'Virtual Appointment' ? (
            <Button
              label="Switch to In-Person"
              type="link"
              className="tw-text-sm tw-text-center tw-px-3"
              onClick={() => {
                (window as any).dataLayer.push({
                  event: 'appointment_form',
                  showroom_page_section: 'select date and time',
                  showroom_action: 'switch to in-person',
                  showroom_location: 'virtual',
                  general_event_details: 'Undefined',
                });
                setInfo('activePage', 1);
              }}
            />
          ) : (
            <>
              {isCity && (
                <Button
                  label="Join Waitlist"
                  type="link"
                  href="https://docs.google.com/forms/d/e/1FAIpQLScyq0GYQYMXx1G8v6EAJ0hNvjJE9MtZQX9INknJlaxm66nM9Q/viewform"
                  className="tw-text-sm tw-text-center max-[360px]:tw-pr-1 tw-pr-3"
                  target="_blank"
                  onClick={() => {
                    (window as any).dataLayer.push({
                      event: 'appointment_form',
                      showroom_page_section: 'select date and time',
                      showroom_action: 'waitlist form',
                      showroom_location: info.selectedShowroomStore.modalName,
                      general_event_details: 'Undefined',
                    });
                  }}
                />
              )}
              {isCity && (
                <Button
                  label="Change Location"
                  type="link"
                  className="tw-text-sm tw-text-center max-[360px]:tw-px-1 tw-px-3"
                  onClick={() => {
                    (window as any).dataLayer.push({
                      event: 'appointment_form',
                      showroom_page_section: 'select date and time',
                      showroom_action: 'change store',
                      showroom_location: info.selectedShowroomStore.modalName,
                      general_event_details: 'Undefined',
                    });
                    setInfo('activePage', 1);
                  }}
                />
              )}

              {info.selectedServiceName !== 'Drop Off' && info.selectedServiceName !== 'Pick Up' ? (
                <Button
                  label={isCity ? 'Switch to Virtual' : `Switch to ${info.prevSelectedShowroomStore.modalName}`}
                  type="link"
                  className="tw-text-sm tw-text-center max-[360px]:tw-pl-1 tw-pl-3"
                  onClick={() => {
                    setIsLoading(true);
                    if (isCity) {
                      (window as any).dataLayer.push({
                        event: 'appointment_form',
                        showroom_page_section: 'select date and time',
                        showroom_action: `switch to virtual`,
                        showroom_location: info.prevSelectedShowroomStore.modalName,
                        general_event_details: 'Undefined',
                      });
                      setInfo('selectedShowroomStore', {
                        modalName: 'Virtual Appointment',
                        displayName1: 'Virtual Appointment',
                        displayName2: '',
                        addresLine1: '',
                        addressLine2: '',
                        id: virtualAppointmentID,
                        timezoneId: '553',
                        timezoneAbreviation: 'PT',
                        city: '',
                        state: '',
                        zipCode: '',
                      });
                      setInfo('selectedTime', null);
                      setInfo('selectedTimeId', null);
                      getSetsterAvailabilityDateTimes(
                        info.selectedServiceId,
                        virtualAppointmentID ?? '',
                        format(new Date(), 'yyyy-MM-dd'),
                        // eslint-disable-next-line no-nested-ternary
                        info.selectedTimezoneId !== '0' ? info.selectedTimezoneId : '553'
                      );
                    } else {
                      (window as any).dataLayer.push({
                        event: 'appointment_form',
                        showroom_page_section: 'select date and time',
                        showroom_action: `switch to showroom|${info.prevSelectedShowroomStore.modalName}`,
                        showroom_location: 'virtual',
                        general_event_details: 'Undefined',
                      });
                      getSetsterAvailabilityDateNightTimes(
                        info.selectedServiceDateNightId,
                        info.prevSelectedShowroomStore.id,
                        format(new Date(), 'yyyy-MM-dd'),
                        '0'
                      );

                      getSetsterAvailabilityDateTimes(
                        info.selectedServiceId,
                        info.prevSelectedShowroomStore.id,
                        format(new Date(), 'yyyy-MM-dd'),
                        '0'
                      );
                      setInfo('selectedTime', null);
                      setInfo('selectedTimeId', null);
                      setInfo('selectedShowroomStore', info.prevSelectedShowroomStore);
                    }
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
